import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button, message, Modal, Radio, Checkbox } from 'antd';
import { UploadOutlined, DeleteOutlined, PlusOutlined, SwapOutlined, CompressOutlined } from '@ant-design/icons';
import { uploadWebImages, updateLogosInTravelsConfig, deleteWebImageFromBucket } from "../../actions/operator-web-config.action";
import { getWebSiteUrlForStorage } from "../../utils/constant";
import { isMasterUser } from "../../utils/utilities";

const LOGO_KEYS = ['logo', 'agent-portal-logo'];

const GCS_BASE_URL = 'https://storage.googleapis.com/operator-websites';
const EXTENSIONS_PNG_FIRST = ['.png'];
const EXTENSIONS_WEBP_FIRST = ['.webp'];

const getImageUrl = (imageType) => {
    const webSiteUrl = getWebSiteUrlForStorage();
    if (!webSiteUrl) return null;
    return `${GCS_BASE_URL}/${webSiteUrl}/${imageType}`;
};

const isLogoOrFavicon = (v) => {
    const t = (v || '').toLowerCase();
    return t === 'logo' || t === 'favicon' || t.includes('logo') || t.includes('favicon');
};

const getExtForType = (type) => (isLogoOrFavicon(type) ? '.png' : '.webp');

const getExtensionsForSlot = (imageVariable) =>
    isLogoOrFavicon(imageVariable) ? EXTENSIONS_PNG_FIRST : EXTENSIONS_WEBP_FIRST;

const FIXED_IMAGE_TYPES = [
    { key: 'logo', label: 'Logo' },
    { key: 'agent-portal-logo', label: 'Agent Portal Logo' },
    { key: 'favicon', label: 'Favicon' },
    { key: 'home-banner', label: 'Hero Image' },
    { key: 'home-page-background', label: 'Home Page About Us Image' },
    { key: 'home-contact-image', label: 'Home Contact Image' },
    { key: 'poster', label: 'Poster for pop up'}
];

const INITIAL_FLEET_COUNT = 4;
const MIN_FLEET_IMAGES = 4;
const MAX_FLEET_IMAGES = 12;

const checkFleetImageExists = (index) =>
    new Promise((resolve) => {
        const key = `fleet-image${index}`;
        const baseUrl = getImageUrl(key);
        if (!baseUrl) {
            resolve(false);
            return;
        }
        const url = `${baseUrl}${getExtForType(key)}`;
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });

/** Find how many consecutive fleet-image1, fleet-image2, ... exist; stop at first missing. */
const detectExistingFleetCount = async () => {
    for (let i = 1; i <= MAX_FLEET_IMAGES; i++) {
        const exists = await checkFleetImageExists(i);
        if (!exists) return i - 1;
    }
    return MAX_FLEET_IMAGES;
};

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/heic', 'image/heif'];
const VALID_IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|svg|heic|heif)$/i;

const isValidImage = (file) =>
    (file && VALID_IMAGE_TYPES.includes(file.type)) || (file?.name && VALID_IMAGE_EXT.test(file.name));

const shouldConvertToWebp = (imageVariable) => !isLogoOrFavicon(imageVariable);

const RASTER_TYPES_FOR_WEBP = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];

/** Re-encoding at full resolution rarely shrinks an already-compressed photo, so cap dimensions too. */
const DEFAULT_MAX_DIMENSION = 1920;
const MAX_DIMENSION_BY_TYPE = {
    'home-banner': 1920,
    'home-page-background': 1600,
    'home-contact-image': 1280,
    poster: 1280,
};
const FLEET_MAX_DIMENSION = 1280;
/** Stop lowering quality once the encode is under this size. */
const WEBP_TARGET_BYTES = 250 * 1024;
const WEBP_QUALITY_STEPS = [0.8, 0.7, 0.6, 0.5];

const getMaxDimensionForType = (type) => {
    const t = (type || '').toLowerCase();
    if (t.startsWith('fleet-image')) return FLEET_MAX_DIMENSION;
    return MAX_DIMENSION_BY_TYPE[t] || DEFAULT_MAX_DIMENSION;
};

/** canvas.toBlob silently falls back to PNG when webp encoding is unsupported, which inflates the file. */
let webpEncodeSupport = null;
const canEncodeWebp = () => {
    if (webpEncodeSupport !== null) return Promise.resolve(webpEncodeSupport);
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        if (typeof canvas.toBlob !== 'function') {
            webpEncodeSupport = false;
            resolve(false);
            return;
        }
        canvas.toBlob(
            (blob) => {
                webpEncodeSupport = !!blob && blob.type === 'image/webp';
                resolve(webpEncodeSupport);
            },
            'image/webp',
            0.8
        );
    });
};

const loadImageFromFile = (file) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('decode-failed'));
        };
        img.src = url;
    });

const encodeCanvasToWebp = (canvas, quality) =>
    new Promise((resolve) => {
        canvas.toBlob(
            (blob) => resolve(blob && blob.type === 'image/webp' ? blob : null),
            'image/webp',
            quality
        );
    });

/**
 * Converts a raster image to webp, downscaling and stepping quality down until the
 * output is under WEBP_TARGET_BYTES. Resolves { file, converted, reason }.
 */
const convertImageToWebp = async (file, outputBaseName) => {
    const type = (file.type || '').toLowerCase();
    const isWebpInput = type === 'image/webp';
    if (!RASTER_TYPES_FOR_WEBP.includes(type)) {
        return { file, converted: false, reason: 'unsupported-type' };
    }
    if (!(await canEncodeWebp())) {
        return { file, converted: false, reason: 'no-webp-encoder' };
    }

    let img;
    try {
        img = await loadImageFromFile(file);
    } catch (e) {
        return { file, converted: false, reason: 'decode-failed' };
    }

    const srcWidth = img.naturalWidth || img.width;
    const srcHeight = img.naturalHeight || img.height;
    if (!srcWidth || !srcHeight) {
        return { file, converted: false, reason: 'decode-failed' };
    }

    const maxDimension = getMaxDimensionForType(outputBaseName);
    const scale = Math.min(1, maxDimension / Math.max(srcWidth, srcHeight));
    const width = Math.max(1, Math.round(srcWidth * scale));
    const height = Math.max(1, Math.round(srcHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    let best = null;
    for (const quality of WEBP_QUALITY_STEPS) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await encodeCanvasToWebp(canvas, quality);
        if (!blob) break;
        if (!best || blob.size < best.size) best = blob;
        if (blob.size <= WEBP_TARGET_BYTES) break;
    }

    if (!best) {
        return { file, converted: false, reason: 'encode-failed' };
    }

    // A webp input that is already smaller than anything we can produce is left alone.
    if (isWebpInput && scale === 1 && best.size >= file.size) {
        return { file, converted: false, reason: 'already-optimal' };
    }

    const base = outputBaseName || file.name.replace(/\.[^.]+$/, '') || 'image';
    return {
        file: new File([best], `${base}.webp`, { type: 'image/webp' }),
        converted: true,
    };
};

const convertImagesForUpload = (images) =>
    Promise.all(
        images.map(async ({ type, file }) => {
            if (!shouldConvertToWebp(type)) return { type, file, conversionFailed: false };
            const { file: outFile, converted, reason } = await convertImageToWebp(file, type);
            const conversionFailed =
                !converted && reason !== 'already-optimal' && reason !== 'unsupported-type';
            return { type, file: outFile, conversionFailed };
        })
    );

/** Fetches an already-uploaded webp back out of the bucket so it can be re-encoded. */
const fetchBucketWebp = async (imageVariable) => {
    const base = getImageUrl(imageVariable);
    if (!base) return null;
    const response = await fetch(`${base}.webp?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob?.size) return null;
    // GCS may serve these as octet-stream; the slot only ever stores webp here.
    return new File([blob], `${imageVariable}.webp`, { type: 'image/webp' });
};

const uploadSingleImage = (type, file) =>
    new Promise((resolve, reject) => {
        uploadWebImages([{ type, file }], () => resolve(), (errMsg) => reject(new Error(errMsg || 'Upload failed')));
    });

const RESIZE_SKIPPED = 'skipped';
const RESIZE_MISSING = 'missing';
const RESIZE_FAILED = 'failed';
const RESIZE_READY = 'ready';
const RESIZE_UPLOADED = 'uploaded';

/**
 * Re-encodes bucket images that are still above WEBP_TARGET_BYTES using the current
 * downscale + quality-step logic. Nothing is uploaded here — a READY row carries the
 * converted file so the caller can upload it after the user confirms.
 */
const prepareResizedImages = async (imageVariables, onProgress = () => {}) => {
    const results = [];
    for (const type of imageVariables) {
        let row;
        try {
            // eslint-disable-next-line no-await-in-loop
            const existing = await fetchBucketWebp(type);
            if (!existing) {
                row = { type, status: RESIZE_MISSING };
            } else if (existing.size <= WEBP_TARGET_BYTES) {
                row = { type, status: RESIZE_SKIPPED, before: existing.size };
            } else {
                // eslint-disable-next-line no-await-in-loop
                const { file: converted, converted: didConvert, reason } = await convertImageToWebp(existing, type);
                if (!didConvert || converted.size >= existing.size) {
                    row = { type, status: RESIZE_SKIPPED, before: existing.size, reason: reason || 'no-gain' };
                } else {
                    row = { type, status: RESIZE_READY, before: existing.size, after: converted.size, file: converted };
                }
            }
        } catch (e) {
            row = { type, status: RESIZE_FAILED, error: e?.message || 'Failed' };
        }
        results.push(row);
        onProgress(row, results.length, imageVariables.length);
    }
    return results;
};

const getUploadExtension = (type, file) => {
    if (isLogoOrFavicon(type)) return '.png';
    const ft = (file?.type || '').toLowerCase();
    if (ft === 'image/svg+xml') return '.svg';
    if (ft === 'image/heic' || ft === 'image/heif') return '.heic';
    return '.webp';
};

const renameFileForUpload = (file, type) => {
    const ext = getUploadExtension(type, file);
    const name = `${type}${ext}`;
    return new File([file], name, { type: file.type || 'application/octet-stream' });
};

const ImageSlot = React.memo(function ImageSlot({
    imageVariable,
    label,
    selectedFile,
    previewUrl,
    baseUrl,
    extensions,
    cacheRef,
    setInputRef,
    getInputRef,
    onFileSelect,
    onFileDrop,
    onRemoveFile,
    onCacheUrl,
    onCacheInvalidate,
    onEnlarge,
    isSelectedForBulkDelete,
    onToggleBulkDelete,
    cacheBustKey,
}) {
    const [extIndex, setExtIndex] = useState(0);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const cachedUrl = cacheRef?.current?.[imageVariable];
    const currentUrl = cachedUrl
        ? cachedUrl
        : baseUrl && extIndex < extensions.length
            ? `${baseUrl}${extensions[extIndex]}?t=${cacheBustKey ?? 0}`
            : null;
    const showExistingImage = baseUrl && (cachedUrl || (currentUrl && !loadFailed));

    const handleImageLoad = useCallback(() => {
        if (currentUrl) onCacheUrl(imageVariable, currentUrl);
    }, [currentUrl, imageVariable, onCacheUrl]);

    const handleImageError = useCallback(() => {
        if (cachedUrl) {
            onCacheInvalidate(imageVariable);
            setExtIndex(0);
            setLoadFailed(false);
            return;
        }
        if (extIndex < extensions.length - 1) {
            setExtIndex((prev) => prev + 1);
        } else {
            setLoadFailed(true);
        }
    }, [cachedUrl, imageVariable, extIndex, extensions.length, onCacheInvalidate]);

    const handleSlotClick = useCallback(() => {
        getInputRef?.(imageVariable)?.click();
    }, [imageVariable, getInputRef]);

    const handleImageClick = useCallback(
        (e) => {
            e.stopPropagation();
            const url = selectedFile && previewUrl ? previewUrl : (showExistingImage ? currentUrl : null);
            if (url) onEnlarge?.(imageVariable, label, url);
        },
        [imageVariable, label, selectedFile, previewUrl, showExistingImage, currentUrl, onEnlarge]
    );

    const handleToggleBulkDelete = useCallback(
        (e) => {
            e.stopPropagation();
            onToggleBulkDelete?.(imageVariable, label, e.target.checked);
        },
        [imageVariable, label, onToggleBulkDelete]
    );

    const handleChange = useCallback(
        (e) => {
            onFileSelect(imageVariable, e);
        },
        [imageVariable, onFileSelect]
    );

    const handleRemove = useCallback(
        (e) => {
            e.stopPropagation();
            onRemoveFile(imageVariable);
        },
        [imageVariable, onRemoveFile]
    );

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            const file = e.dataTransfer?.files?.[0];
            if (file) onFileDrop?.(imageVariable, file);
        },
        [imageVariable, onFileDrop]
    );

    return (
        <div className="image-slot-wrapper">
            <div className="slot-label">{label}</div>
            <div
                className={`image-slot${isDragging ? ' image-slot-dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={(el) => setInputRef?.(imageVariable, el)}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/heic,image/heif,.jpg,.jpeg,.png,.gif,.webp,.svg,.heic,.heif"
                    className="hidden-input"
                    onChange={handleChange}
                />
                {selectedFile && previewUrl ? (
                    <div className="slot-preview">
                        <img src={previewUrl} alt={label} onClick={handleImageClick} className="slot-img-clickable" />
                        <div className="slot-overlay" onClick={handleSlotClick}>
                            <span className="slot-filename">{selectedFile.name}</span>
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                className="remove-btn"
                                onClick={handleRemove}
                            />
                        </div>
                    </div>
                ) : showExistingImage ? (
                    <div className="slot-preview">
                        <img
                            src={currentUrl}
                            alt={label}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            onClick={handleImageClick}
                            className="slot-img-clickable"
                        />
                        <div className="slot-overlay slot-overlay-existing" onClick={handleSlotClick}>
                            <span className="placeholder-hint">Click or drop to replace</span>
                        </div>
                    </div>
                ) : (
                    <div className="slot-placeholder" onClick={handleSlotClick}>
                        <UploadOutlined className="placeholder-icon" />
                        <span className="placeholder-hint">Click or drop image</span>
                    </div>
                )}
            </div>
            {showExistingImage && onToggleBulkDelete && (
                <Checkbox
                    checked={!!isSelectedForBulkDelete}
                    onChange={handleToggleBulkDelete}
                    className="bulk-delete-checkbox below-slot"
                >
                    Select to delete from bucket
                </Checkbox>
            )}
        </div>
    );
});

const getLogoUrlForType = (imageType, cacheRef, getImageUrlFn) => {
    const fromCache = cacheRef?.current?.[imageType];
    if (fromCache) return fromCache;
    const base = getImageUrlFn(imageType);
    return base ? `${base}${getExtForType(imageType)}` : null;
};

const stripCacheQuery = (url) =>
    url && typeof url === 'string' ? url.replace(/\?.*$/, '') : url;

const formatKb = (bytes) => `${(bytes / 1024).toFixed(0)} KB`;

const labelForType = (type) =>
    FIXED_IMAGE_TYPES.find((t) => t.key === type)?.label ||
    (type.startsWith('fleet-image') ? `Fleet Image ${type.replace('fleet-image', '')}` : type);

const resizeRowColor = (row) => {
    if (row.uploadError || row.status === RESIZE_FAILED) return '#cf1322';
    if (row.status === RESIZE_READY) return '#389e0d';
    if (row.status === RESIZE_UPLOADED) return '#1677ff';
    return '#8c8c8c';
};

const describeResizeRow = (row) => {
    if (row.status === RESIZE_READY) {
        const sizes = `${formatKb(row.before)} -> ${formatKb(row.after)}`;
        return row.uploadError ? `${sizes} — ${row.uploadError}, retry` : sizes;
    }
    if (row.status === RESIZE_UPLOADED) return `${formatKb(row.before)} -> ${formatKb(row.after)} (uploaded)`;
    if (row.status === RESIZE_MISSING) return 'not uploaded';
    if (row.status === RESIZE_FAILED) return `failed: ${row.error}`;
    if (row.reason) return `kept (${formatKb(row.before)}, ${row.reason})`;
    return `already optimised (${formatKb(row.before)})`;
};

export const OperatorWebsiteImagesTab = ({ onPreviewImageOverridesChange }) => {
    const [selectedFiles, setSelectedFiles] = useState({});
    const [fleetCount, setFleetCount] = useState(INITIAL_FLEET_COUNT);
    const [uploading, setUploading] = useState(false);
    const [previewUrls, setPreviewUrls] = useState({});
    const [printLogoModalVisible, setPrintLogoModalVisible] = useState(false);
    const [printLogoChoice, setPrintLogoChoice] = useState('logo');
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [selectedForBulkDelete, setSelectedForBulkDelete] = useState({});
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [cacheBustKey, setCacheBustKey] = useState(0);
    const [resizing, setResizing] = useState(false);
    const [resizeProgress, setResizeProgress] = useState(null);
    const [resizeResults, setResizeResults] = useState(null);
    const [selectedResizeTypes, setSelectedResizeTypes] = useState({});
    const [reUploading, setReUploading] = useState(false);
    const showResizeAction = useMemo(() => {
        try {
            return isMasterUser();
        } catch (e) {
            return false;
        }
    }, []);
    const pendingLogosRef = useRef(null);
    const fileInputRefs = useRef({});
    const loadedImageUrlCache = useRef({});
    const previewUrlRef = useRef({});

    const setCacheForUploaded = useCallback((uploadedTypes) => {
        if (!uploadedTypes?.length) return;
        const webSiteUrl = getWebSiteUrlForStorage();
        if (!webSiteUrl) return;
        const base = `${GCS_BASE_URL}/${webSiteUrl}`;
        const t = `?t=${Date.now()}`;
        uploadedTypes.forEach((imageVariable) => {
            loadedImageUrlCache.current[imageVariable] = `${base}/${imageVariable}${getExtForType(imageVariable)}${t}`;
        });
    }, []);

    const fleetImageKeys = useMemo(
        () =>
            Array.from({ length: fleetCount }, (_, i) => ({
                key: `fleet-image${i + 1}`,
                label: `Fleet Image ${i + 1}`,
            })),
        [fleetCount]
    );

    useEffect(() => {
        let cancelled = false;
        detectExistingFleetCount().then((existingCount) => {
            if (cancelled) return;
            const initialFleetCount = Math.max(MIN_FLEET_IMAGES, existingCount);
            setFleetCount(initialFleetCount);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const cur = previewUrlRef.current;
        const files = selectedFiles;
        const next = {};
        Object.entries(files).forEach(([key, file]) => {
            if (cur[key]?.file === file) {
                next[key] = cur[key].url;
            } else {
                if (cur[key]) URL.revokeObjectURL(cur[key].url);
                cur[key] = { file, url: URL.createObjectURL(file) };
                next[key] = cur[key].url;
            }
        });
        Object.keys(cur).forEach((key) => {
            if (!(key in files)) {
                URL.revokeObjectURL(cur[key].url);
                delete cur[key];
            }
        });
        setPreviewUrls((prev) => (Object.keys(next).length ? next : {}));
    }, [selectedFiles]);

    useEffect(() => {
        onPreviewImageOverridesChange?.(previewUrls);
    }, [previewUrls, onPreviewImageOverridesChange]);

    useEffect(() => {
        return () => {
            const cur = previewUrlRef.current;
            Object.values(cur).forEach((o) => URL.revokeObjectURL(o.url));
            Object.keys(cur).forEach((k) => delete cur[k]);
        };
    }, []);

    const handleFileSelect = useCallback((imageVariable, event) => {
        const file = event.target.files?.[0];
        if (file && isValidImage(file)) {
            setSelectedFiles((prev) => ({ ...prev, [imageVariable]: file }));
        } else if (file) {
            message.warning('Please select a valid image (jpg, png, gif, webp, svg, heic)');
        }
        event.target.value = '';
    }, []);

    const handleFileDrop = useCallback((imageVariable, file) => {
        if (file && isValidImage(file)) {
            setSelectedFiles((prev) => ({ ...prev, [imageVariable]: file }));
        } else if (file) {
            message.warning('Please drop a valid image (jpg, png, gif, webp, svg, heic)');
        }
    }, []);

    const removeFile = useCallback((imageVariable) => {
        setSelectedFiles((prev) => {
            const next = { ...prev };
            delete next[imageVariable];
            return next;
        });
    }, []);

    const hasLogoChange = useCallback((uploadedTypes) =>
        LOGO_KEYS.some((key) => uploadedTypes.includes(key)), []);

    const sendLogosConfig = useCallback((logosPayload, printLogoUrl) => {
        const logos = {
            logoUrl: stripCacheQuery(logosPayload.logoUrl) || null,
            agentPortalLogo: stripCacheQuery(logosPayload.agentPortalLogo) || null,
            printLogo: stripCacheQuery(printLogoUrl) || null,
        };
        updateLogosInTravelsConfig(logos, () => {
            message.success('Logos config updated.');
        }, () => {});
    }, []);

    const handlePrintLogoModalOk = useCallback(() => {
        const pending = pendingLogosRef.current;
        if (!pending) {
            setPrintLogoModalVisible(false);
            return;
        }
        const printLogo = printLogoChoice === 'logo' ? pending.logoUrl : pending.agentPortalLogo;
        if (printLogo) {
            sendLogosConfig(pending, printLogo);
        }
        pendingLogosRef.current = null;
        setPrintLogoModalVisible(false);
    }, [printLogoChoice, sendLogosConfig]);

    const handleUploadAll = useCallback(() => {
        const entries = Object.entries(selectedFiles);
        if (entries.length === 0) {
            message.warning('Please select at least one image to upload');
            return;
        }
        setUploading(true);
        const images = entries.map(([type, file]) => ({ type, file }));
        convertImagesForUpload(images)
            .then((convertedImages) => {
                const failedTypes = convertedImages.filter((i) => i.conversionFailed).map((i) => i.type);
                if (failedTypes.length) {
                    message.warning(
                        `Could not convert to webp: ${failedTypes.join(', ')}. Uploading the original file(s) instead.`
                    );
                }
                const renamedImages = convertedImages.map(({ type, file }) => ({
                    type,
                    file: renameFileForUpload(file, type),
                }));
                const uploadedTypes = renamedImages.map(({ type }) => type);
                uploadWebImages(
                    renamedImages,
                    () => {
                        setCacheForUploaded(uploadedTypes);
                        setUploading(false);
                        setSelectedFiles({});
                        message.success(`All ${convertedImages.length} image(s) uploaded successfully`);

                        if (!hasLogoChange(uploadedTypes)) return;
                        const webSiteUrl = getWebSiteUrlForStorage();
                        if (!webSiteUrl) return;
                        const base = `${GCS_BASE_URL}/${webSiteUrl}`;
                        const urlFor = (type) => `${base}/${type}${getExtForType(type)}`;
                        const logoUrl = uploadedTypes.includes('logo') ? urlFor('logo') : getLogoUrlForType('logo', loadedImageUrlCache, getImageUrl);
                        const agentPortalLogo = uploadedTypes.includes('agent-portal-logo')
                            ? urlFor('agent-portal-logo')
                            : loadedImageUrlCache.current['agent-portal-logo'] || null;

                        if (logoUrl && agentPortalLogo) {
                            pendingLogosRef.current = { logoUrl, agentPortalLogo };
                            setPrintLogoChoice('logo');
                            setPrintLogoModalVisible(true);
                        } else if (logoUrl) {
                            sendLogosConfig({ logoUrl, agentPortalLogo: logoUrl }, logoUrl);
                        } else if (agentPortalLogo) {
                            sendLogosConfig({ logoUrl: logoUrl || null, agentPortalLogo }, agentPortalLogo);
                        }
                    },
                    (errMsg) => {
                        setUploading(false);
                        message.error(errMsg || 'Upload failed');
                    }
                );
            })
            .catch((err) => {
                setUploading(false);
                message.error(err?.message || 'Failed to prepare images');
            });
    }, [selectedFiles, setCacheForUploaded, hasLogoChange, sendLogosConfig]);

    const addFleetImage = useCallback(() => {
        if (fleetCount < MAX_FLEET_IMAGES) {
            setFleetCount((prev) => prev + 1);
        } else {
            message.info(`Maximum ${MAX_FLEET_IMAGES} fleet images allowed`);
        }
    }, [fleetCount]);

    const setInputRef = useCallback((imageVariable, el) => {
        if (el) fileInputRefs.current[imageVariable] = el;
    }, []);

    const getInputRef = useCallback((imageVariable) => fileInputRefs.current[imageVariable], []);

    const onCacheUrl = useCallback((imageVariable, url) => {
        loadedImageUrlCache.current[imageVariable] = url;
    }, []);

    const onCacheInvalidate = useCallback((imageVariable) => {
        delete loadedImageUrlCache.current[imageVariable];
    }, []);

    /** Master-user only: re-encode oversized bucket images. Converts only — upload is confirmed separately. */
    const handleResizeExisting = useCallback(async () => {
        const candidates = [
            ...FIXED_IMAGE_TYPES.map(({ key }) => key).filter((key) => !isLogoOrFavicon(key)),
            ...fleetImageKeys.map(({ key }) => key),
        ];
        if (!getWebSiteUrlForStorage()) {
            message.warning('Operator website not configured');
            return;
        }
        setResizing(true);
        setResizeResults(null);
        setSelectedResizeTypes({});
        setResizeProgress({ done: 0, total: candidates.length, current: candidates[0] });
        try {
            const results = await prepareResizedImages(candidates, (row, done, total) => {
                setResizeProgress({ done, total, current: candidates[done] || null });
            });
            const ready = results.filter((r) => r.status === RESIZE_READY);
            setResizeResults(results);
            // Everything that can be re-uploaded starts selected.
            setSelectedResizeTypes(ready.reduce((acc, r) => ({ ...acc, [r.type]: true }), {}));
            if (!ready.length) message.info('No image needed re-sizing');
        } catch (e) {
            message.error(e?.message || 'Failed to re-size images');
        } finally {
            setResizing(false);
            setResizeProgress(null);
        }
    }, [fleetImageKeys]);

    const toggleResizeSelection = useCallback((type, checked) => {
        setSelectedResizeTypes((prev) => {
            const next = { ...prev };
            if (checked) next[type] = true;
            else delete next[type];
            return next;
        });
    }, []);

    const readyResizeRows = useMemo(
        () => (resizeResults || []).filter((r) => r.status === RESIZE_READY),
        [resizeResults]
    );

    const selectedResizeRows = useMemo(
        () => readyResizeRows.filter((r) => selectedResizeTypes[r.type]),
        [readyResizeRows, selectedResizeTypes]
    );

    const toggleAllResizeSelection = useCallback(
        (checked) => {
            setSelectedResizeTypes(
                checked ? readyResizeRows.reduce((acc, r) => ({ ...acc, [r.type]: true }), {}) : {}
            );
        },
        [readyResizeRows]
    );

    /** Uploads only the rows the user kept ticked in the re-size results modal. */
    const handleReUploadSelected = useCallback(async () => {
        if (!selectedResizeRows.length) return;
        setReUploading(true);
        const uploaded = [];
        const failed = [];
        for (const row of selectedResizeRows) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await uploadSingleImage(row.type, row.file);
                uploaded.push(row.type);
            } catch (e) {
                failed.push({ type: row.type, error: e?.message || 'Upload failed' });
            }
        }
        setReUploading(false);
        setResizeResults((prev) =>
            (prev || []).map((row) => {
                if (uploaded.includes(row.type)) return { ...row, status: RESIZE_UPLOADED, uploadError: null };
                const fail = failed.find((f) => f.type === row.type);
                // Stay READY so the row keeps its checkbox and the user can retry.
                if (fail) return { ...row, uploadError: fail.error };
                return row;
            })
        );
        setSelectedResizeTypes((prev) => {
            const next = { ...prev };
            uploaded.forEach((type) => delete next[type]);
            return next;
        });
        if (uploaded.length) {
            uploaded.forEach((type) => onCacheInvalidate(type));
            setCacheForUploaded(uploaded);
            setCacheBustKey(Date.now());
            message.success(`Re-uploaded ${uploaded.length} image(s)`);
        }
        if (failed.length) {
            message.error(`Failed to re-upload ${failed.length} image(s)`);
        }
    }, [selectedResizeRows, onCacheInvalidate, setCacheForUploaded]);

    const requestBulkDeleteFromBucket = useCallback(() => {
        const items = Object.entries(selectedForBulkDelete).map(([imageVariable, label]) => ({ imageVariable, label }));
        if (items.length === 0) return;
        setImageToDelete({ bulk: true, items });
        setDeleteModalVisible(true);
    }, [selectedForBulkDelete]);

    const cancelDeleteFromBucket = useCallback(() => {
        if (!deleting) {
            setDeleteModalVisible(false);
            setImageToDelete(null);
        }
    }, [deleting]);

    const confirmDeleteFromBucket = useCallback(() => {
        if (!imageToDelete?.items?.length) return;
        setDeleting(true);
        const items = imageToDelete.items;
        const imageVariables = items.map(({ imageVariable }) => `${imageVariable}${getExtForType(imageVariable)}`);
        deleteWebImageFromBucket(
            imageVariables,
            () => {
                items.forEach(({ imageVariable }) => onCacheInvalidate(imageVariable));
                setCacheBustKey(Date.now());
                setDeleting(false);
                setSelectedForBulkDelete({});
                setDeleteModalVisible(false);
                setImageToDelete(null);
                message.success(`${items.length} image(s) deleted from bucket`);
            },
            (errMsg) => {
                setDeleting(false);
                message.error(errMsg || 'Failed to delete image(s) from bucket');
            }
        );
    }, [imageToDelete, onCacheInvalidate]);

    const handleEnlarge = useCallback((imageVariable, label, url) => {
        setEnlargedImage({ imageVariable, label, url });
    }, []);

    const handleReplaceFromEnlarge = useCallback(() => {
        if (!enlargedImage) return;
        getInputRef(enlargedImage.imageVariable)?.click();
        setEnlargedImage(null);
    }, [enlargedImage, getInputRef]);

    const toggleBulkDelete = useCallback((imageVariable, label, checked) => {
        setSelectedForBulkDelete((prev) => {
            const next = { ...prev };
            if (checked) next[imageVariable] = label;
            else delete next[imageVariable];
            return next;
        });
    }, []);

    const selectedCount = Object.keys(selectedFiles).length;
    const hasSelectedFiles = selectedCount > 0;

    const slotProps = useMemo(
        () => ({
            cacheRef: loadedImageUrlCache,
            setInputRef,
            getInputRef,
            onFileSelect: handleFileSelect,
            onFileDrop: handleFileDrop,
            onRemoveFile: removeFile,
            onCacheUrl,
            onCacheInvalidate,
            onEnlarge: handleEnlarge,
            onToggleBulkDelete: toggleBulkDelete,
        }),
        [handleFileSelect, handleFileDrop, removeFile, setInputRef, getInputRef, onCacheUrl, onCacheInvalidate, handleEnlarge, toggleBulkDelete]
    );

    const slotPropsFor = useCallback(
        (key, label) => ({
            key,
            imageVariable: key,
            label,
            selectedFile: selectedFiles[key],
            previewUrl: previewUrls[key],
            baseUrl: getImageUrl(key),
            extensions: getExtensionsForSlot(key),
            isSelectedForBulkDelete: !!selectedForBulkDelete[key],
            cacheBustKey,
            ...slotProps,
        }),
        [selectedFiles, previewUrls, selectedForBulkDelete, cacheBustKey, slotProps]
    );

    const bulkDeleteCount = Object.keys(selectedForBulkDelete).length;

    return (
        <div className="tab-content tab-content-images">
            <Modal
                title="Print logo"
                visible={printLogoModalVisible}
                onOk={handlePrintLogoModalOk}
                onCancel={() => {
                    setPrintLogoModalVisible(false);
                    pendingLogosRef.current = null;
                }}
                okText="Apply"
            >
                <p>Which logo should act as print logo?</p>
                <Radio.Group
                    value={printLogoChoice}
                    onChange={(e) => setPrintLogoChoice(e.target.value)}
                    style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                    <Radio value="logo">Logo</Radio>
                    <Radio value="agent-portal-logo">Agent Portal Logo</Radio>
                </Radio.Group>
            </Modal>
            <Modal
                title={enlargedImage ? enlargedImage.label : ''}
                visible={!!enlargedImage}
                onCancel={() => setEnlargedImage(null)}
                footer={
                    enlargedImage ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={() => setEnlargedImage(null)}>Close</Button>
                            <Button type="primary" icon={<SwapOutlined />} onClick={handleReplaceFromEnlarge}>
                                Replace image
                            </Button>
                        </div>
                    ) : null
                }
                width="90vw"
                style={{ top: 24 }}
                bodyStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}
            >
                {enlargedImage && (
                    <img
                        src={enlargedImage.url}
                        alt={enlargedImage.label}
                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    />
                )}
            </Modal>
            <Modal
                title="Delete image(s) from bucket?"
                visible={deleteModalVisible}
                onCancel={cancelDeleteFromBucket}
                footer={null}
                closable={!deleting}
                maskClosable={!deleting}
            >
                {imageToDelete?.items?.length > 0 && (
                    <>
                        <p>
                            Are you sure you want to delete <strong>{imageToDelete.items.length} image(s)</strong> from the bucket?
                        </p>
                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={cancelDeleteFromBucket} disabled={deleting}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                danger
                                loading={deleting}
                                onClick={confirmDeleteFromBucket}
                            >
                                Delete from bucket
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
            <Modal
                title="Re-sized images"
                visible={!!resizeResults}
                onCancel={() => setResizeResults(null)}
                closable={!reUploading}
                maskClosable={!reUploading}
                footer={[
                    <Button key="close" onClick={() => setResizeResults(null)} disabled={reUploading}>
                        Close
                    </Button>,
                    <Button
                        key="reupload"
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={reUploading}
                        disabled={selectedResizeRows.length === 0}
                        onClick={handleReUploadSelected}
                    >
                        Re upload ({selectedResizeRows.length})
                    </Button>,
                ]}
            >
                {resizeResults && (
                    <>
                        <p style={{ marginBottom: 12, color: '#64748b' }}>
                            {readyResizeRows.length > 0
                                ? 'Nothing has been uploaded yet. Untick any image you want to keep as-is, then re-upload.'
                                : 'No image needed re-sizing.'}
                        </p>
                        {readyResizeRows.length > 0 && (
                            <Checkbox
                                checked={selectedResizeRows.length === readyResizeRows.length}
                                indeterminate={
                                    selectedResizeRows.length > 0 &&
                                    selectedResizeRows.length < readyResizeRows.length
                                }
                                onChange={(e) => toggleAllResizeSelection(e.target.checked)}
                                disabled={reUploading}
                                style={{ marginBottom: 8, fontWeight: 600 }}
                            >
                                Select all
                            </Checkbox>
                        )}
                        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {resizeResults.map((row) => {
                                const selectable = row.status === RESIZE_READY;
                                return (
                                    <div
                                        key={row.type}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 12,
                                            padding: '4px 0',
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectable && !!selectedResizeTypes[row.type]}
                                            disabled={!selectable || reUploading}
                                            onChange={(e) => toggleResizeSelection(row.type, e.target.checked)}
                                        >
                                            {labelForType(row.type)}
                                        </Checkbox>
                                        <span style={{ color: resizeRowColor(row) }}>
                                            {describeResizeRow(row)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </Modal>
            <p className="tab-description">
                Click an image to enlarge; click the overlay to replace. Select existing images with the checkbox to delete multiple from the bucket.
            </p>
            <div className="images-section">
                <h3 className="section-title">Brand & Layout</h3>
                <div className="image-grid">
                    {FIXED_IMAGE_TYPES.map(({ key, label }) => (
                        <ImageSlot {...slotPropsFor(key, label)} />
                    ))}
                </div>
            </div>
            <div className="images-section fleet-section">
                <div className="section-header">
                    <h3 className="section-title">Fleet Images</h3>
                    <div className="fleet-actions">
                        <Button
                            type="primary"
                            ghost
                            icon={<PlusOutlined />}
                            onClick={addFleetImage}
                            disabled={fleetCount >= MAX_FLEET_IMAGES}
                            className="action-btn"
                        >
                            Add more ({fleetCount}/{MAX_FLEET_IMAGES})
                        </Button>
                    </div>
                </div>
                <div className="image-grid">
                    {fleetImageKeys.map(({ key, label }) => (
                        <ImageSlot {...slotPropsFor(key, label)} />
                    ))}
                </div>
            </div>
            <div className="upload-footer">
                <Button
                    type="primary"
                    size="large"
                    icon={<UploadOutlined />}
                    onClick={handleUploadAll}
                    loading={uploading}
                    disabled={!hasSelectedFiles}
                    className="upload-all-btn"
                >
                    {hasSelectedFiles ? `Upload ${selectedCount} image(s)` : 'Select images to upload'}
                </Button>
                {showResizeAction && (
                    <Button
                        type="default"
                        size="large"
                        icon={<CompressOutlined />}
                        onClick={handleResizeExisting}
                        loading={resizing}
                        disabled={uploading}
                        className="resize-images-btn"
                    >
                        {resizing && resizeProgress
                            ? `Re-sizing ${resizeProgress.done}/${resizeProgress.total}...`
                            : 'Re-size image'}
                    </Button>
                )}
                <Button
                    type="default"
                    size="large"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={requestBulkDeleteFromBucket}
                    disabled={bulkDeleteCount === 0}
                    className="delete-selected-btn"
                >
                    Delete selected from bucket{bulkDeleteCount > 0 ? ` (${bulkDeleteCount})` : ''}
                </Button>
            </div>
        </div>
    );
};
